import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { selectMyCandidatesGridData } from './MyCandidates.selector';
import { XGridChipCell } from 'app/components/Common/XGrid/cells/XGridChipCell';
import { convertToFormat } from 'app/helpers/dateHelper';
import { PlacementFacilityLinkCell } from './cells/PlacementFacilityLinkCell';
import { CandidateLinkCell } from './cells/CandidateLinkCell';
import { IRecruitmentRetentionPlacement } from 'app/models/MyCandidates/RecruitmentRetention';
import { usePromiseTracker } from 'react-promise-tracker';

const getStatusColor = (value: string) => {
  const v = (value || '').toLowerCase();
  if (v.includes('good to go') || v.includes('active')) return '#2E7D32';
  if (v.includes('booked') || v.includes('assignment complete')) return '#006FB9';
  if (v.includes('cancel') || v.includes('audit')) return '#E65100';
  return '#666';
};

const useStyles = makeStyles()(theme => ({
  container: {
    paddingTop: theme.spacing(3),
  },
}));

export const CandidatesGrid = ({ totalCandidates = 0 }: { totalCandidates?: number }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const gridData = useSelector(selectMyCandidatesGridData);
  const { promiseInProgress } = usePromiseTracker({ area: 'MyCandidates-recruitment-retention', delay: 0 });

  const rows = React.useMemo(
    () =>
      gridData.map((p: IRecruitmentRetentionPlacement, idx: number) => ({
        id: p.placementId ?? p.candidateId ?? idx,
        candidate: p.candidateName ?? '-',
        candidateId: p.candidateId,
        placementId: p.placementId ?? (p as any).placementID ?? undefined,
        facilityId: p.facilityId ?? (p as any).facilityID ?? undefined,
        placementID: (p as any).placementID ?? p.placementId,
        facilityID: (p as any).facilityID ?? p.facilityId,
        skillset: p.skillset ?? '-',
        brand: p.brand ?? '-',
        city: p.city ?? '-',
        state: p.state ?? '-',
        availableToStart: p.availableToStart ?? p.availableToStartDate
          ? convertToFormat(p.availableToStart ?? p.availableToStartDate, 'MM/DD/YYYY')
          : '-',
        endDateMod: p.endDateModEffective
          ? convertToFormat(p.endDateModEffective, 'MM/DD/YYYY')
          : '-',
        lastContacted: p.lastContacted ?? p.recruiterLastAttempt
          ? convertToFormat(p.lastContacted ?? p.recruiterLastAttempt, 'MM/DD/YYYY')
          : '-',
        candidateStatus: p.candidateStatus ?? '-',
        placementStatus: p.placementStatus ?? '-',
        brandId: p.brandId ?? (p as any).brandId ?? (p as any).brandID ?? (p as any).BrandId ?? 0,
      })),
    [gridData],
  );

  const columns = React.useMemo(
    () => [
      {
        field: 'candidateId',
        headerName: t('home.recWorkDeskHeaders.candidateId'),
        width: 100,
        minWidth: 90,
        renderCell: CandidateLinkCell('candidateId'),
      },
      {
        field: 'candidate',
        headerName: t('home.recWorkDeskHeaders.candidate'),
        width: 200,
        minWidth: 160,
        flex: 1,
        renderCell: CandidateLinkCell('candidate'),
      },
      { field: 'skillset', headerName: t('home.recWorkDeskHeaders.skillSet'), width: 200 },
      { field: 'brand', headerName: 'Brand', width: 120 },
      { field: 'city', headerName: t('home.recWorkDeskHeaders.city'), width: 120 },
      { field: 'state', headerName: t('home.recWorkDeskHeaders.state'), width: 80 },
      {
        field: 'placementId',
        headerName: t('home.recWorkDeskHeaders.placementId'),
        width: 90,
        renderCell: PlacementFacilityLinkCell('placementId'),
      },
      {
        field: 'facilityId',
        headerName: t('home.recWorkDeskHeaders.facilityId'),
        width: 90,
        renderCell: PlacementFacilityLinkCell('facilityId'),
      },
      { field: 'availableToStart', headerName: t('home.recWorkDeskHeaders.availableToStart'), width: 140 },
      { field: 'endDateMod', headerName: t('home.recWorkDeskHeaders.endDateMod'), width: 130 },
      { field: 'lastContacted', headerName: t('home.recWorkDeskHeaders.lastContacted'), width: 130 },
      {
        field: 'candidateStatus',
        headerName: 'Candidate Status',
        width: 150,
        renderCell: XGridChipCell({ size: 'small', dynamicColor: p => getStatusColor(p.value as string) }),
      },
      {
        field: 'placementStatus',
        headerName: 'Placement Status',
        width: 170,
        renderCell: XGridChipCell({ size: 'small', dynamicColor: p => getStatusColor(p.value as string) }),
      },
    ],
    [t],
  );

  return (
    <Box className={classes.container}>
      <XGrid
        title="Candidates"
        rows={rows}
        defaultColumns={columns}
        columns={columns}
        autoHeight={false}
        height={400}
        rowHeight={62}
        sortingMode="client"
        loading={promiseInProgress}
        rowsPerPageOptions={[20, 50, 100, 250]}
        checkboxSelection
        toolbar
        toolbarProps={{
          exportButton: true,
          columnSelector: true,
        }}
      />
    </Box>
  );
};
